/**
 * One importer per bank.
 *
 * Every bank exports its own shape of spreadsheet and has its own habits, and
 * the one that matters most is **when a transaction is dated**. A card payment
 * may appear on the day it was authorised in Monday's export and on the day it
 * settled in Friday's, so the same payment arrives twice, two days apart, and a
 * same-day check sees two different transactions.
 *
 * Rather than guess at that in general, each bank gets a file here saying how
 * its statements read and which rows may have moved. `generic` is the fallback
 * that knows nothing and therefore assumes nothing.
 *
 * ## Adding a bank
 *
 * Copy `generic.js`, give it an `id`, and fill in what differs:
 *
 * - `read(rows, options)` — usually `readStatement` with a column mapping it
 *   already knows, so the user is not asked to point at the date column.
 * - `matchRule(existing)` — `{ keyOf, withinDays, lenientWhen }`, asked for with
 *   the ledger in hand so a bank can look at it first. `keyOf` is everything but
 *   the date; `lenientWhen(row)` marks the rows that are allowed to have moved.
 *
 * Then add it to `IMPORTERS` below and give it a name under `importer.<id>` in
 * both catalogues. Which bank you are with is a setting, not a guess: a wrong
 * importer silently mis-reads a statement, and that is not worth risking to
 * save one dropdown.
 */
import { SAME_DAY } from '../statement.js'
import generic from './generic.js'
import intesa from './intesa.js'

export const IMPORTERS = [generic, intesa]

export const GENERIC = generic.id

export const importerFor = (id) => IMPORTERS.find((i) => i.id === id) ?? generic

/**
 * The rule this importer uses to decide a row is already in the ledger.
 *
 * It is asked for with the ledger in hand, because a bank may need to look at
 * it first — Intesa has to work out your surname before it can tell a settled
 * row from an unsettled one.
 */
export const matchRuleFor = (importer, existing) =>
  typeof importer?.matchRule === 'function' ? importer.matchRule(existing) : SAME_DAY

/** The header row as text, for an importer that wants to check the layout. */
export const headerTexts = (rows, headerRow = 0) =>
  (rows?.[headerRow] ?? []).map((cell) => String(cell ?? '').trim()).filter(Boolean)
